import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_QUALITY_RECORD_KINDS,
  ALL_QUALITY_RECORD_STATUSES,
  type QualityRecordView,
} from '@lims/shared-types';
import { clientsApi, equipmentApi, qualityApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  DataTable,
  FilterBar,
  PageHeader,
  SearchField,
  SelectField,
  StatusBadge,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ActDialog } from './ActDialog.js';
import { qualityStatusTone } from './operations-ui.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * Complaints, observations and non-conformities (Phase 12, D163).
 *
 * A person holding only `quality.raise` sees the raising form and nothing of
 * the register: raising is not reading (D166). The server enforces both.
 */
export function QualityPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();

  const canView = can(PERMISSIONS.QUALITY_VIEW);
  const canRaise = can(PERMISSIONS.QUALITY_RAISE);

  const [search, setSearch] = useState('');
  // The dashboard links here with `?kind=`; an unknown value is ignored.
  const [searchParams] = useSearchParams();
  const requestedKind = searchParams.get('kind');
  const [kind, setKind] = useState<string | null>(
    requestedKind && (ALL_QUALITY_RECORD_KINDS as readonly string[]).includes(requestedKind) ? requestedKind : null,
  );
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [raising, setRaising] = useState(false);

  const records = useQuery({
    queryKey: ['quality-records', { page, search, kind, status }],
    queryFn: () =>
      qualityApi.list({
        page,
        ...(search ? { search } : {}),
        ...(kind ? { kind } : {}),
        ...(status ? { status } : {}),
      }),
    enabled: canView,
  });

  // Optional links, offered only to someone who may read what they point at.
  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
    enabled: raising && can(PERMISSIONS.CLIENTS_VIEW),
  });
  const instruments = useQuery({
    queryKey: ['equipment', 'selectable'],
    queryFn: () => equipmentApi.list({ pageSize: 100 }),
    enabled: raising && can(PERMISSIONS.EQUIPMENT_VIEW),
  });

  const columns: readonly DataColumn<QualityRecordView>[] = [
    {
      key: 'number',
      header: t('quality.number'),
      render: (row) => (
        <span className="text-default-900" dir="ltr">
          {row.recordNumber}
        </span>
      ),
    },
    { key: 'kind', header: t('quality.kind'), render: (row) => t(`qualityKind.${row.kind}`) },
    { key: 'title', header: t('quality.recordTitle'), render: (row) => <span className="text-default-800">{row.title}</span> },
    {
      key: 'raisedOn',
      header: t('quality.raisedOn'),
      render: (row) => formatDate(row.raisedOn, { locale }),
    },
    {
      key: 'responsible',
      header: t('quality.responsible'),
      render: (row) => row.responsibleUser?.displayName ?? t('common.notSet'),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <span className="flex flex-wrap gap-inline">
          <StatusBadge label={t(`qualityStatus.${row.status}`)} tone={qualityStatusTone(row.status)} />
          {row.isOverdue ? <StatusBadge label={t('quality.overdue')} tone="danger" /> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/quality/${row.id}`)}>
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  const raiseDialog = (
    <ActDialog
      isOpen={raising}
      onClose={() => setRaising(false)}
      title={t('quality.raiseTitle')}
      hint={t('quality.raiseHint')}
      submitLabel={t('quality.raise')}
      fields={[
        {
          name: 'kind',
          label: t('quality.kind'),
          kind: 'select',
          isRequired: true,
          options: ALL_QUALITY_RECORD_KINDS.map((value) => ({ value, label: t(`qualityKind.${value}`) })),
        },
        { name: 'title', label: t('quality.recordTitle'), kind: 'text', isRequired: true },
        { name: 'description', label: t('quality.description'), kind: 'textarea', isRequired: true },
        ...(can(PERMISSIONS.CLIENTS_VIEW)
          ? [
              {
                name: 'clientId',
                label: t('quality.client'),
                kind: 'select' as const,
                description: t('common.optional'),
                options: (clients.data?.data ?? []).map((client) => ({
                  value: client.id,
                  label: client.name ?? client.clientNumber,
                })),
              },
            ]
          : []),
        ...(can(PERMISSIONS.EQUIPMENT_VIEW)
          ? [
              {
                name: 'equipmentId',
                label: t('quality.equipment'),
                kind: 'select' as const,
                description: t('common.optional'),
                options: (instruments.data?.data ?? []).map((item) => ({
                  value: item.id,
                  label: item.equipmentCode,
                })),
              },
            ]
          : []),
      ]}
      onSubmit={async (values) => {
        const created = await qualityApi.raise({
          kind: values.kind,
          title: values.title?.trim(),
          description: values.description?.trim(),
          ...(values.clientId ? { clientId: values.clientId } : {}),
          ...(values.equipmentId ? { equipmentId: values.equipmentId } : {}),
        });
        show({ tone: 'success', message: t('quality.raised', { number: created.recordNumber }) });
        await queryClient.invalidateQueries({ queryKey: ['quality-records'] });
        if (canView) navigate(`/quality/${created.id}`);
      }}
    />
  );

  if (!canView) {
    return (
      <>
        <PageHeader
          title={t('quality.raiseTitle')}
          subtitle={t('quality.raiseOnlySubtitle')}
          breadcrumbs={[{ label: t('nav.quality') }]}
          actions={
            canRaise ? (
              <ActionButton priority="primary" onPress={() => setRaising(true)}>
                {t('quality.raise')}
              </ActionButton>
            ) : null
          }
        />
        <Alert tone="info" title={t('quality.raiseOnlyNotice')} />
        {raiseDialog}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('quality.title')}
        subtitle={t('quality.subtitle')}
        breadcrumbs={[{ label: t('nav.quality') }]}
        actions={
          canRaise ? (
            <ActionButton priority="primary" onPress={() => setRaising(true)}>
              {t('quality.raise')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel
        toolbar={
          <FilterBar>
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t('quality.searchPlaceholder')}
            />
            <SelectField
              label={t('quality.kind')}
              value={kind}
              onChange={(value) => {
                setKind(value);
                setPage(1);
              }}
              placeholder={t('common.all')}
              options={ALL_QUALITY_RECORD_KINDS.map((value) => ({ value, label: t(`qualityKind.${value}`) }))}
            />
            <SelectField
              label={t('common.status')}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              placeholder={t('common.all')}
              options={ALL_QUALITY_RECORD_STATUSES.map((value) => ({ value, label: t(`qualityStatus.${value}`) }))}
            />
          </FilterBar>
        }
      >
        <DataTable
          label={t('quality.title')}
          columns={columns}
          rows={records.data?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={records.isLoading}
          error={records.error}
          onRetry={() => void records.refetch()}
          emptyContent={search || kind || status ? t('quality.noneFound') : t('quality.noneYet')}
          page={page}
          totalPages={records.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>
      <p className="mt-gutter text-caption text-default-600">{t('quality.notAVerdict')}</p>
      {raiseDialog}
    </>
  );
}
